import React, { Component } from 'react';
import { skillsRef } from '../config/firebase';
import { onValue, set, child, update, remove } from 'firebase/database';
import { showToast } from '../actions/toastBus';
import PageLoader from './PageLoader';
import {
    LuSave,
    LuPlus,
    LuTag,
    LuPercent,
    LuPencilLine,
    LuSparkles,
    LuX,
    LuTrash2
} from 'react-icons/lu';
import '../stylesheet/Skills.css';
import '../stylesheet/style.css';

export default class Skills extends Component {

    constructor(props) {
        super(props);
        this.state = {
            skills: [],
            contentLoading: true,
            isLoggedIn: false,
            draftSkill: { name: '', percentage: 0, category: '' },
            modalCustomCategory: false,
            // null = the popup is adding a new skill; otherwise the id
            // (real Firebase index) of the skill being edited.
            editingId: null,
            modalOpen: false,
            modalClosing: false
        };
    };

    // Dropdown options come straight from the categories already sitting
    // in the DB (every skill's own `category` field) instead of a
    // hardcoded list, so it always reflects what's actually there.
    getCategoryOptions = () => {
        const skills = Array.isArray(this.state.skills) ? this.state.skills : [];
        const categories = new Set();
        skills.forEach((skill) => {
            if (skill && skill.category) categories.add(skill.category);
        });
        categories.add('Other');
        return Array.from(categories).sort((a, b) => a.localeCompare(b));
    };

    getGroupedSkills = () => {
        const skills = Array.isArray(this.state.skills) ? this.state.skills : [];
        const groups = [];
        const indexByCategory = {};
        skills.forEach((skill, id) => {
            if (!skill) return;
            const category = skill.category || 'Other';
            if (!(category in indexByCategory)) {
                indexByCategory[category] = groups.length;
                groups.push({ category, items: [] });
            }
            groups[indexByCategory[category]].items.push({ skill, id });
        });
        return groups;
    };

    chooseColor = (colorType) => {
        switch(colorType) {
            case 1:
                return "#00ff43";
            case 2:
                return "#25c0be";
            case 3:
                return '#25c0be';
            case 4:
                return '#2555c0';
            case 5:
                return '#ffc300';
            case 6:
                return '#ff0057';
            case 7:
                return '#7a08e4';
            case 8:
                return '#ed1c6f';
            case 9:
                return '#e8e925';
            case 10:
                return '#74c54f';
            default:
                return '#add8e6';
        }
    }

    componentDidMount() {
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(skillsRef, (snapshot) => {
            let skills = snapshot.val();
            this.setState({
                skills: skills,
                contentLoading: false
            });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key === 'Escape' && this.state.modalOpen) {
            this.closeModal();
        }
    }

    // Same popup handles add and edit: null editingId = blank form for a
    // new skill, otherwise the form is pre-filled from that card and
    // saves back to its own Firebase key instead of creating a new one.
    openAddModal = () => {
        this.setState({
            editingId: null,
            draftSkill: { name: '', percentage: 0, category: '' },
            modalCustomCategory: false,
            modalOpen: true,
            modalClosing: false
        });
    }

    openEditModal = (id, skill) => {
        this.setState({
            editingId: id,
            draftSkill: {
                name: skill.name || '',
                percentage: skill.percentage,
                category: skill.category || 'Other'
            },
            modalCustomCategory: false,
            modalOpen: true,
            modalClosing: false
        });
    }

    closeModal = () => {
        // Match the exit-animation timing (see .skill-modal-* in
        // Skills.css) before actually unmounting.
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ modalOpen: false, modalClosing: false, editingId: null });
        }, 320);
    }

    handleDraftChange = (field, value) => {
        this.setState((prev) => ({
            draftSkill: { ...prev.draftSkill, [field]: value }
        }));
    }

    // The dropdown's "+ Add new category" option switches the field to a
    // free-text input instead (for the first skill in a not-yet-existing
    // category); picking a real option switches it back.
    handleCategorySelect = (value) => {
        if (value === '__new__') {
            this.setState({ modalCustomCategory: true });
            this.handleDraftChange('category', '');
        } else {
            this.setState({ modalCustomCategory: false });
            this.handleDraftChange('category', value);
        }
    }

    saveSkill = () => {
        const { name, percentage, category } = this.state.draftSkill;
        if (!name || percentage < 0 || percentage > 100) return showToast('Enter a name and a percentage between 0-100.', 'error');
        update(skillsRef, {
            [this.state.editingId]: { name, percentage: parseInt(percentage, 10), category: category || 'Other' }
        })
            .then(() => {
                this.closeModal();
                showToast('Skill updated', 'success');
            })
            .catch((error) => {
                console.error('Error updating skill:', error);
                showToast('Could not update this skill - please try again.', 'error');
            });
    }

    addNewSkill = () => {
        const { name, percentage, category } = this.state.draftSkill;
        if (!name || percentage < 0 || percentage > 100) return showToast('Enter a name and a percentage between 0-100.', 'error');

        const id = this.state.skills.length.toString(); // Firebase keys must be strings

        const skillData = {
            name,
            percentage: parseInt(percentage, 10),
            category: category || 'Other'
        };

        // Save using index as key
        set(child(skillsRef, id), skillData)
            .then(() => {
                this.closeModal();
                showToast('New skill added', 'success');
            })
            .catch(error => {
                console.error("Error saving skill:", error);
                showToast('Could not add this skill - please try again.', 'error');
            });
    }

    deleteSkill = () => {
        if (this.state.editingId === null) return;
        if (!window.confirm("Delete this skill? This can't be undone.")) return;

        remove(child(skillsRef, this.state.editingId.toString()))
            .then(() => {
                this.closeModal();
                showToast('Skill deleted', 'success');
            })
            .catch((error) => {
                console.error("Error deleting skill:", error);
                showToast('Could not delete this skill - please try again.', 'error');
            });
    }

    handleModalSubmit = () => {
        if (this.state.editingId !== null) {
            this.saveSkill();
        } else {
            this.addNewSkill();
        }
    }

    render() {
        const { isLoggedIn, contentLoading, draftSkill, modalOpen, modalClosing, editingId, modalCustomCategory } = this.state;
        return (
            <div className='base'>
                <div className='main-content-outer'>
                <h1 className="sr-only">Skills</h1>
                <span className="page-watermark" aria-hidden="true">Skills</span>
                {contentLoading && <PageLoader />}
                {!contentLoading && (
                <>
                {this.getGroupedSkills().map(({ category, items }) => (
                    <div className="skill-category" key={category}>
                        <div className="skill-category-header">
                            <span className="skill-category-title">{category}</span>
                            <span className="skill-category-count">{items.length}</span>
                        </div>
                        <div className="mains">
                            {items.map(({ skill, id }) => {
                                let skillPercentage = !skill && typeof skill.percentage !== 'number' ? 0 : skill.percentage
                                let randomNumber = Math.floor((Math.random() * 10) + 1);
                                let colors = this.chooseColor(randomNumber);
                                return isLoggedIn ? (
                                    <div
                                        className="card skill-card skill-card-clickable"
                                        key={id}
                                        onClick={() => this.openEditModal(id, skill)}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(id, skill))}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Edit ${skill.name}`}
                                    >
                                        <div className="skill-card-top">
                                            <span className="skill-name">{skill.name}</span>
                                            <span className="skill-percent" style={{ color: colors }}>{skill.percentage}%</span>
                                        </div>
                                        <div className="skill-bar-track">
                                            <div className="skill-bar-fill" style={{ width: `${skillPercentage}%`, background: colors }}></div>
                                        </div>
                                        <span className="skill-edit-hint"><LuPencilLine /> Edit</span>
                                    </div>
                                ) : (
                                    <div className="card skill-card" key={id}>
                                        <div className="skill-card-top">
                                            <span className="skill-name">{skill.name}</span>
                                            <span className="skill-percent" style={{ color: colors }}>{skill.percentage}%</span>
                                        </div>
                                        <div className="skill-bar-track">
                                            <div className="skill-bar-fill" style={{ width: `${skillPercentage}%`, background: colors }}></div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}

                {isLoggedIn && (
                    <button
                        type="button"
                        className="skill-fab"
                        onClick={this.openAddModal}
                        aria-label="Add new skill"
                    >
                        <LuPlus />
                    </button>
                )}

                {modalOpen && (
                    <div
                        className={`skill-modal-backdrop${modalClosing ? ' is-closing' : ''}`}
                        onClick={this.closeModal}
                    >
                        <div className="skill-modal-panel" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="skill-modal-close"
                                onClick={this.closeModal}
                                aria-label="Close"
                            >
                                <LuX />
                            </button>
                            <span className="skill-add-eyebrow">
                                {editingId !== null ? <><LuPencilLine /> Editing</> : <><LuSparkles /> Admin</>}
                            </span>
                            <h3 className="skill-add-heading">
                                {editingId !== null ? 'Edit skill' : 'Add a new skill'}
                            </h3>
                            {editingId === null && (
                                <p className="skill-add-sub">It'll show up under the category you give it, or "Other" if left blank.</p>
                            )}
                            <label className="skill-field">
                                <span className="skill-field-label">Skill name</span>
                                <input
                                    type="text"
                                    placeholder="e.g. React"
                                    value={draftSkill.name}
                                    onChange={(e) => this.handleDraftChange('name', e.target.value)}
                                    className="skill-field-input"
                                    autoFocus
                                />
                            </label>
                            <div className="skill-field-row">
                                <label className="skill-field">
                                    <span className="skill-field-label"><LuTag /> Category</span>
                                    {modalCustomCategory ? (
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="New category name"
                                            value={draftSkill.category}
                                            onChange={(e) => this.handleDraftChange('category', e.target.value)}
                                            className="skill-field-input"
                                        />
                                    ) : (
                                        <select
                                            value={draftSkill.category || ''}
                                            onChange={(e) => this.handleCategorySelect(e.target.value)}
                                            className="skill-field-input skill-field-select"
                                        >
                                            <option value="" disabled>Select category</option>
                                            {this.getCategoryOptions().map((cat) => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                            <option value="__new__">+ Add new category</option>
                                        </select>
                                    )}
                                </label>
                                <label className="skill-field skill-field-narrow">
                                    <span className="skill-field-label"><LuPercent /> Level</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="0-100"
                                        value={draftSkill.percentage}
                                        onChange={(e) => this.handleDraftChange('percentage', e.target.value)}
                                        className="skill-field-input"
                                    />
                                </label>
                            </div>
                            <div className="skill-modal-actions">
                                <button onClick={this.handleModalSubmit} className="app-form-button skill-save-cta">
                                    {editingId !== null ? <><LuSave /> Save changes</> : <><LuPlus /> Add skill</>}
                                </button>
                                {editingId !== null && (
                                    <button
                                        type="button"
                                        onClick={this.deleteSkill}
                                        className="skill-delete-cta"
                                    >
                                        <LuTrash2 /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                </>
                )}
                </div>
            </div>
        )
    }
}
